import { database } from './config';
import { ref, get, set, runTransaction } from 'firebase/database';
import { formatActDataForElo } from './eloService';
import { getPlayersWithEloRatings, setPlayerElo, deletePlayerElo } from './eloManagement';

const DEFAULT_ELO = 450; // Match sophisticated system
const HISTORICAL_K_FACTOR = 24; // Reduced K-factor for historical recalculation

// Helper functions for ELO calculation
function calculateExpectedScore(playerRating, avgOthersRating) {
  return 1 / (1 + Math.pow(10, (avgOthersRating - playerRating) / 400));
}

function calculateActualScore(finish, numPlayers) {
  return (numPlayers - finish) / (numPlayers - 1);
}

function calculateNewRating(currentRating, expectedScore, actualScore, kFactor) {
  const change = Math.round(kFactor * (actualScore - expectedScore));
  return currentRating + change;
}

// Note: Now using sophisticated ELO system from eloService.js instead of simple calculations

/**
 * Recalculate all ELO ratings from scratch based on historical data
 * This will process all ACT entries in chronological order
 * @param {boolean} updateFirebase - Whether to update Firebase with new ratings
 * @returns {Promise<Object>} Object with recalculated ratings and history
 */
export async function recalculateAllEloRatings(updateFirebase = false) {
  console.log('🔄 Starting complete ELO recalculation from historical data');
  
  try {
    // Fetch all ACT history from Firebase
    const historyRef = ref(database, 'act-history');
    const snapshot = await get(historyRef);
    
    if (!snapshot.exists()) {
      console.log('❌ No historical data found');
      return { ratings: {}, histories: {} };
    }
    
    const historyData = snapshot.val();
    const historyArray = Object.entries(historyData).map(([id, data]) => ({
      ...data,
      firebaseId: id
    }));
    
    // Sort by timestamp (oldest first)
    historyArray.sort((a, b) => (a.timestamp || 0) - (b.timestamp || 0));
    
    console.log(`📊 Processing ${historyArray.length} ACT entries`);
    
    // Initialize player ratings and histories
    const playerRatings = {};
    const playerHistories = {};
    const raceCount = {};
    
    // Process each ACT entry in chronological order
    historyArray.forEach((actData, actIndex) => {
      const actDate = actData.date || new Date(actData.timestamp).toISOString();
      const raceResults = formatActDataForElo(actData);
      
      console.log(`Processing ACT ${actIndex + 1}/${historyArray.length} from ${actDate.split('T')[0]} (${raceResults.length} races)`);
      
      // Process each race in the ACT
      raceResults.forEach((race) => {
        const playerIds = Object.keys(race.players);
        const numPlayers = playerIds.length;
        
        if (numPlayers < 2) {
          console.log(`  Skipping race with only ${numPlayers} player(s)`);
          return;
        }
        
        // Initialize ratings for new players
        playerIds.forEach(playerId => {
          if (!(playerId in playerRatings)) {
            playerRatings[playerId] = DEFAULT_ELO;
            playerHistories[playerId] = [{
              date: actDate,
              elo: DEFAULT_ELO,
              event: 'Initial',
              change: 0
            }];
            raceCount[playerId] = 0;
          }
        });
        
        // Store current ratings before updates
        const preRaceRatings = {};
        playerIds.forEach(playerId => {
          preRaceRatings[playerId] = playerRatings[playerId];
        });
        
        // Calculate new ratings for this race
        const newRatings = {};
        
        playerIds.forEach(playerId => {
          const playerRating = preRaceRatings[playerId];
          const finish = race.players[playerId].finish;
          
          // Calculate average rating of other players
          const otherRatings = playerIds
            .filter(id => id !== playerId)
            .map(id => preRaceRatings[id]);
          const avgOthersRating = otherRatings.reduce((sum, r) => sum + r, 0) / otherRatings.length;
          
          // Calculate expected and actual scores
          const expectedScore = calculateExpectedScore(playerRating, avgOthersRating);
          const actualScore = calculateActualScore(finish, numPlayers);
          
          // Calculate new rating with reduced historical impact
          newRatings[playerId] = calculateNewRating(playerRating, expectedScore, actualScore, HISTORICAL_K_FACTOR);
          raceCount[playerId]++;
        });
        
        // Update ratings and history
        Object.entries(newRatings).forEach(([playerId, newRating]) => {
          const change = newRating - playerRatings[playerId];
          playerRatings[playerId] = newRating;
          
          // Add to history
          playerHistories[playerId].push({
            date: actDate,
            elo: newRating,
            change,
            cup: race.cup,
            raceNum: race.raceNum,
            finish: race.players[playerId].finish,
            event: `${race.cup} Race ${race.raceNum}`,
            raceNumber: raceCount[playerId]
          });
        });
      });
    });
    
    console.log('✅ ELO recalculation complete');
    console.log(`📊 Calculated ratings for ${Object.keys(playerRatings).length} players`);
    
    // Log top 5 players
    const sortedPlayers = Object.entries(playerRatings)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 5);
    
    console.log('\n🏆 Top 5 Players:');
    sortedPlayers.forEach(([playerId, rating], index) => {
      const races = raceCount[playerId];
      console.log(`  ${index + 1}. ${playerId}: ${rating} (${races} races)`);
    });
    
    // Optionally update Firebase with new ratings
    if (updateFirebase) {
      console.log('\n📤 Updating Firebase with recalculated ratings...');
      
      await Promise.all(
        Object.entries(playerRatings).map(async ([playerId, rating]) => {
          // Use history-based setPlayerElo instead of direct ELO path
          await setPlayerElo(playerId, rating);
        })
      );
      
      console.log('✅ Firebase ELO ratings updated successfully');
    }
    
    return {
      ratings: playerRatings,
      histories: playerHistories,
      raceCount
    };
  } catch (error) {
    console.error('❌ Error recalculating ELO ratings:', error);
    throw error;
  }
}

/**
 * Process all historical scorecards and update ELO ratings based on actual player participation
 * This uses the player mapping system to correctly identify who played in each race
 * @param {boolean} updateFirebase - Whether to update Firebase with new ratings
 * @returns {Promise<Object>} Object with recalculated ratings and history
 */
export async function processHistoricalScorecardsForElo(updateFirebase = false) {
  console.log('🏁 Processing all historical scorecards for ELO calculations...');
  
  try {
    // Fetch all ACT history from Firebase
    const historyRef = ref(database, 'act-history');
    const snapshot = await get(historyRef);
    
    if (!snapshot.exists()) {
      console.log('❌ No historical scorecard data found');
      return { ratings: {}, histories: {}, processed: 0 };
    }
    
    const historyData = snapshot.val();
    const historyArray = Object.entries(historyData).map(([id, data]) => ({
      ...data,
      firebaseId: id
    }));
    
    // Sort by timestamp (oldest first)
    historyArray.sort((a, b) => (a.timestamp || 0) - (b.timestamp || 0));
    
    console.log(`📊 Found ${historyArray.length} historical ACT entries to process`);
    
    // Get current player mappings
    const currentPlayersElo = await getPlayersWithEloRatings(historyArray);
    console.log('👥 Current player mappings:', Object.keys(currentPlayersElo));
    
    // Initialize ELO ratings for all discovered players
    const playerRatings = {};
    const playerHistories = {};
    const playerRaceCount = {};
    
    // Set initial ratings for all players found in history
    // Only include players who have ELO ratings in the database
    Object.keys(currentPlayersElo).forEach(playerName => {
      const existingElo = currentPlayersElo[playerName].elo;
      
      // Only process players who have ELO ratings in the database
      if (existingElo !== null && typeof existingElo === 'number') {
        playerRatings[playerName] = existingElo;
        playerHistories[playerName] = [{
          date: 'Initial',
          elo: existingElo,
          event: 'System Initialization (Database Value)',
          change: 0
        }];
        playerRaceCount[playerName] = 0;
      }
    });
    
    console.log('🔄 Initialized ELO ratings:', playerRatings);
    
    let totalRacesProcessed = 0;
    let actsProcessed = 0;
    
    // Process each ACT entry chronologically
    for (let actIndex = 0; actIndex < historyArray.length; actIndex++) {
      const actData = historyArray[actIndex];
      const actDate = actData.date || new Date(actData.timestamp).toISOString();
      
      console.log(`\n📋 Processing ACT ${actIndex + 1}/${historyArray.length} from ${actDate.split('T')[0]}`);
      
      // Extract actual player participation from this ACT
      const actRaces = extractRacesWithPlayerParticipation(actData);
      console.log(`🏁 Found ${actRaces.length} races in this ACT`);
      
      // Process each race
      for (const race of actRaces) {
        const playerNames = Object.keys(race.players);
        
        // Only include players who have ELO ratings in the database
        const ratedPlayerNames = playerNames.filter(name => playerRatings[name] !== undefined);
        const numRatedPlayers = ratedPlayerNames.length;
        
        if (numRatedPlayers < 2) {
          console.log(`  ⚠️ Skipping race - only ${numRatedPlayers} rated player(s) (${playerNames.length} total players: ${playerNames.join(', ')})`);
          continue;
        }
        
        // Create filtered race data with only rated players
        const ratedRacePlayers = {};
        ratedPlayerNames.forEach(name => {
          ratedRacePlayers[name] = race.players[name];
        });
        
        console.log(`  🏎️ Processing race: ${race.cup} ${race.raceNum} with rated players:`, ratedPlayerNames);
        if (playerNames.length > ratedPlayerNames.length) {
          const unratedPlayers = playerNames.filter(name => !ratedPlayerNames.includes(name));
          console.log(`    ℹ️ Skipping unrated players: ${unratedPlayers.join(', ')}`);
        }
        
        // Calculate ELO changes for this race (only for rated players)
        const eloChanges = calculateEloChangesForRace(ratedRacePlayers, playerRatings);
        
        // Apply changes and update history
        Object.entries(eloChanges).forEach(([playerName, change]) => {
          const oldRating = playerRatings[playerName];
          const newRating = oldRating + change;
          
          playerRatings[playerName] = newRating;
          playerRaceCount[playerName]++;
          
          playerHistories[playerName].push({
            date: actDate,
            elo: newRating,
            change: change,
            cup: race.cup,
            raceNum: race.raceNum,
            finish: race.players[playerName].finish,
            event: `${race.cup} Race ${race.raceNum}`,
            raceNumber: playerRaceCount[playerName]
          });
          
          console.log(`    📊 ${playerName}: ${oldRating} → ${newRating} (${change > 0 ? '+' : ''}${change})`);
        });
        
        totalRacesProcessed++;
      }
      
      actsProcessed++;
    }
    
    console.log('\n✅ Historical processing complete!');
    console.log(`📊 Processed ${actsProcessed} ACTs and ${totalRacesProcessed} races`);
    console.log(`👥 Updated ${Object.keys(playerRatings).length} player ratings`);
    
    // Show final standings
    const sortedPlayers = Object.entries(playerRatings)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 10);
    
    console.log('\n🏆 Top 10 Players After Historical Processing:');
    sortedPlayers.forEach(([playerName, rating], index) => {
      const races = playerRaceCount[playerName];
      console.log(`  ${index + 1}. ${playerName}: ${Math.round(rating)} ELO (${races} races)`);
    });
    
    // Optionally update Firebase with recalculated ratings
    if (updateFirebase) {
      console.log('\n📤 Updating Firebase with recalculated ELO ratings...');
      
      const updates = {};
      Object.entries(playerRatings).forEach(([playerName, rating]) => {
        // Use player name as the Firebase key for now
        updates[playerName] = Math.round(rating);
      });
      
      const eloRef = ref(database, 'ELO');
      await set(eloRef, updates);
      
      console.log('✅ Firebase ELO ratings updated successfully');
    }
    
    return {
      ratings: playerRatings,
      histories: playerHistories,
      raceCount: playerRaceCount,
      processed: totalRacesProcessed,
      acts: actsProcessed
    };
    
  } catch (error) {
    console.error('❌ Error processing historical scorecards:', error);
    throw error;
  }
}

/**
 * Extract races with actual player participation from an ACT entry
 * @param {Object} actData - ACT entry data
 * @returns {Array} Array of races with player participation
 */
function extractRacesWithPlayerParticipation(actData) {
  const races = [];
  
  if (!actData.teams || !actData.races) {
    return races;
  }
  
  // Process each cup in the ACT
  Object.keys(actData.races).forEach(cup => {
    const cupRaces = actData.races[cup];
    
    // Process each race in the cup
    Object.keys(cupRaces || {}).forEach(raceNum => {
      const race = cupRaces[raceNum];
      const raceParticipants = {};
      
      // Check each team's participation in this race
      Object.keys(race || {}).forEach(team => {
        const raceData = race[team];
        
        if (raceData && raceData.racer && raceData.place && raceData.place !== 'X') {
          // Get the actual player name for this team/racer combo
          const playerName = getPlayerNameFromTeamData(actData.teams, team, raceData.racer);
          
          if (playerName) {
            raceParticipants[playerName] = {
              finish: raceData.place,
              team: team,
              racer: raceData.racer
            };
          }
        }
      });
      
      // Only add races with at least 2 participants
      if (Object.keys(raceParticipants).length >= 2) {
        races.push({
          cup,
          raceNum,
          players: raceParticipants
        });
      }
    });
  });
  
  return races;
}

/**
 * Get actual player name from team data
 * @param {Object} teamsData - Teams data from ACT
 * @param {string} team - Team letter (A, B, C, D)
 * @param {string} racer - Racer letter (A, B)
 * @returns {string|null} Player name or null
 */
function getPlayerNameFromTeamData(teamsData, team, racer) {
  if (!teamsData || !teamsData[team] || !teamsData[team].players) {
    return null;
  }
  
  const playerName = teamsData[team].players[racer];
  
  // Skip default placeholder names
  if (!playerName || playerName === `Player ${racer}`) {
    return null;
  }
  
  return playerName;
}

/**
 * Calculate ELO changes for a single race
 * @param {Object} racePlayers - Players and their finishes
 * @param {Object} currentRatings - Current ELO ratings
 * @returns {Object} ELO changes for each player
 */
function calculateEloChangesForRace(racePlayers, currentRatings) {
  const playerNames = Object.keys(racePlayers);
  const numPlayers = playerNames.length;
  const changes = {};
  
  playerNames.forEach(playerName => {
    const playerRating = currentRatings[playerName] || DEFAULT_ELO;
    const finish = racePlayers[playerName].finish;
    
    // Calculate average rating of other players
    const otherRatings = playerNames
      .filter(name => name !== playerName)
      .map(name => currentRatings[name] || DEFAULT_ELO);
    const avgOthersRating = otherRatings.reduce((sum, r) => sum + r, 0) / otherRatings.length;
    
    // Calculate expected and actual scores
    const expectedScore = calculateExpectedScore(playerRating, avgOthersRating);
    const actualScore = calculateActualScore(finish, numPlayers);
    
    // Calculate change with reduced historical impact
    changes[playerName] = Math.round(HISTORICAL_K_FACTOR * (actualScore - expectedScore));
  });
  
  return changes;
}

/**
 * Reset all ELO ratings to default
 * @returns {Promise<void>}
 */
export async function resetAllEloToDefault() {
  console.log('🔄 Resetting all ELO ratings to default');
  
  try {
    // Get all current ELO ratings
    const eloRef = ref(database, 'ELO');
    const snapshot = await get(eloRef);
    
    if (snapshot.exists()) {
      const currentRatings = snapshot.val();
      const playerIds = Object.keys(currentRatings);
      
      // Reset each player to default
      await Promise.all(
        playerIds.map(async (playerId) => {
          // Use history-based setPlayerElo instead of direct ELO path
          await setPlayerElo(playerId, DEFAULT_ELO);
        })
      );
      
      console.log(`✅ Reset ${playerIds.length} players to ${DEFAULT_ELO} ELO`);
    } else {
      console.log('📊 No existing ELO ratings to reset');
    }
  } catch (error) {
    console.error('❌ Error resetting ELO ratings:', error);
    throw error;
  }
}

/**
 * Get ELO statistics
 * @returns {Promise<Object>} Statistics about current ELO ratings
 */
export async function getEloStatistics() {
  try {
    const eloRef = ref(database, 'ELO');
    const snapshot = await get(eloRef);
    
    if (!snapshot.exists()) {
      return {
        totalPlayers: 0,
        averageElo: 0,
        highestElo: { player: null, rating: 0 },
        lowestElo: { player: null, rating: 0 },
        distribution: {}
      };
    }
    
    const ratings = snapshot.val();
    const entries = Object.entries(ratings);
    const values = entries.map(([, rating]) => rating);
    
    // Find highest and lowest
    const [highestPlayer, highestRating] = entries.reduce((max, curr) => 
      curr[1] > max[1] ? curr : max, ['', 0]);
    const [lowestPlayer, lowestRating] = entries.reduce((min, curr) => 
      curr[1] < min[1] ? curr : min, ['', Infinity]);
    
    // Calculate distribution
    const distribution = {
      '200-300': 0,
      '300-400': 0,
      '400-500': 0,
      '500-600': 0,
      '600-700': 0,
      '700-800': 0,
      '800-900': 0,
      '900-1000': 0
    };
    
    values.forEach(rating => {
      const bucket = Math.floor(rating / 100) * 100;
      const key = `${bucket}-${bucket + 100}`;
      if (key in distribution) {
        distribution[key]++;
      }
    });
    
    return {
      totalPlayers: entries.length,
      averageElo: Math.round(values.reduce((sum, r) => sum + r, 0) / values.length),
      highestElo: { player: highestPlayer, rating: highestRating },
      lowestElo: { player: lowestPlayer, rating: lowestRating },
      distribution
    };
  } catch (error) {
    console.error('Error getting ELO statistics:', error);
    throw error;
  }
}

// Note: Historical processing can be triggered via the ELO Admin Panel